interface IconProps {
  name:
    | 'play'
    | 'pause'
    | 'replay'
    | 'forward'
    | 'buffering'
    | 'chevron-down'
    | 'home'
    | 'search'
    | 'library'
    | 'queue'
    | 'settings'
    | 'plus'
    | 'check'
    | 'refresh'
    | 'download'
    | 'downloaded'
    | 'cast'
    | 'cast-connected'
    | 'volume'
    | 'volume-low'
    | 'volume-mute'
    | 'maximize'
    | 'minimize'
    | 'warning'
    | 'chat'
    | 'info'
    | 'x';
  /** Rendered inside skip icons to show the seconds jumped. */
  label?: string;
  size?: number;
}

/**
 * Inline SVG rather than an icon font or sprite sheet: no extra request, no FOUT, and
 * `currentColor` keeps everything theme-aware for free.
 */
export function Icon({ name, label, size = 24 }: IconProps) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': 2,
    'stroke-linecap': 'round' as const,
    'stroke-linejoin': 'round' as const,
    'aria-hidden': true,
    focusable: 'false',
  };

  switch (name) {
    case 'play':
      return (
        <svg {...common} fill="currentColor" stroke="none">
          <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86A1 1 0 0 0 8 5.14z" />
        </svg>
      );
    case 'pause':
      return (
        <svg {...common} fill="currentColor" stroke="none">
          <rect x="6" y="4" width="4" height="16" rx="1" />
          <rect x="14" y="4" width="4" height="16" rx="1" />
        </svg>
      );
    case 'replay':
      return (
        <svg {...common}>
          <path d="M12 5V2L7 6l5 4V7a6 6 0 1 1-6 6" />
          {label && (
            <text x="12" y="16" font-size="7" text-anchor="middle" fill="currentColor" stroke="none">
              {label}
            </text>
          )}
        </svg>
      );
    case 'forward':
      return (
        <svg {...common}>
          <path d="M12 5V2l5 4-5 4V7a6 6 0 1 0 6 6" />
          {label && (
            <text x="12" y="16" font-size="7" text-anchor="middle" fill="currentColor" stroke="none">
              {label}
            </text>
          )}
        </svg>
      );
    case 'buffering':
      return (
        <svg {...common} class="spin">
          <path d="M12 3a9 9 0 1 0 9 9" />
        </svg>
      );
    case 'chevron-down':
      return (
        <svg {...common}>
          <path d="m6 9 6 6 6-6" />
        </svg>
      );
    case 'home':
      return (
        <svg {...common}>
          <path d="M3 10.5 12 3l9 7.5" />
          <path d="M5 9.5V21h14V9.5" />
        </svg>
      );
    case 'search':
      return (
        <svg {...common}>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      );
    case 'library':
      return (
        <svg {...common}>
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      );
    case 'queue':
      return (
        <svg {...common}>
          <path d="M4 6h11M4 12h11M4 18h7" />
          <path d="m17 13 4 2.5-4 2.5z" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'settings':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3.2" />
          <path d="M12 2.5v2.2M12 19.3v2.2M4.2 4.2l1.6 1.6M18.2 18.2l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.2 19.8l1.6-1.6M18.2 5.8l1.6-1.6" />
        </svg>
      );
    case 'plus':
      return (
        <svg {...common}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case 'check':
      return (
        <svg {...common}>
          <path d="m4 12.5 5 5L20 6.5" />
        </svg>
      );
    case 'refresh':
      return (
        <svg {...common}>
          <path d="M21 12a9 9 0 1 1-2.6-6.4" />
          <path d="M21 3v6h-6" />
        </svg>
      );
    case 'download':
      return (
        <svg {...common}>
          <path d="M12 3v12" />
          <path d="m7 10 5 5 5-5" />
          <path d="M4 20h16" />
        </svg>
      );
    case 'downloaded':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" fill="currentColor" stroke="none" />
          <path d="m8 12.2 2.8 2.8L16 9.8" stroke="var(--surface, #fff)" />
        </svg>
      );
    // The platform cast glyph: a screen with the signal arcs in its lower-left corner. Kept
    // recognisable rather than restyled — it is the one control users already know by shape.
    case 'cast':
      return (
        <svg {...common}>
          <path d="M3 8.5V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-7" />
          <path d="M3 12.5a7 7 0 0 1 7 7" />
          <path d="M3 16.5a3 3 0 0 1 3 3" />
          <path d="M3 20h.01" />
        </svg>
      );
    case 'cast-connected':
      return (
        <svg {...common}>
          <path d="M3 8.5V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-7" />
          <path d="M7 8h11a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-4" fill="currentColor" stroke="none" />
          <path d="M3 12.5a7 7 0 0 1 7 7" />
          <path d="M3 16.5a3 3 0 0 1 3 3" />
          <path d="M3 20h.01" />
        </svg>
      );
    case 'volume':
      return (
        <svg {...common}>
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
        </svg>
      );
    case 'volume-low':
      return (
        <svg {...common}>
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
        </svg>
      );
    case 'volume-mute':
      return (
        <svg {...common}>
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
          <line x1="23" y1="9" x2="17" y2="15" />
          <line x1="17" y1="9" x2="23" y2="15" />
        </svg>
      );
    case 'maximize':
      return (
        <svg {...common}>
          <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
        </svg>
      );
    case 'minimize':
      return (
        <svg {...common}>
          <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7" />
        </svg>
      );
    case 'warning':
      return (
        <svg {...common}>
          <path d="M12 3.8 2.6 20h18.8L12 3.8z" />
          <path d="M12 10v4" />
          <path d="M12 17.2v.1" />
        </svg>
      );
    case 'chat':
      return (
        <svg {...common}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      );
    case 'info':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      );
    case 'x':
      return (
        <svg {...common}>
          <path d="M6 6l12 12M18 6 6 18" />
        </svg>
      );
  }
}
